import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Contact } from '@weblite/ts-action/contact'
import { RootState } from '$setup/redux'
import { getSignedUpContactIds } from '$logic/contacts/service'
import { compareString } from '$func/sort'
import { SignedUpContact } from '$logic/contacts/types'
import { useFuse } from '$func/hooks'
import { getUserDisplayName } from '$logic/users'
import { User } from '@weblite/ts-action/user'

const getContactsView = (state: RootState) => state.main.contacts

const getUserFullName = (firstname = '', lastname = '') =>
  [firstname, lastname].filter(Boolean).join(' ')

export function useSortedContacts() {
  const contacts: Record<string, Contact> = useSelector(getContactsView)
  const contactIds = getSignedUpContactIds()
  return useMemo(
    () =>
      contactIds
        .map(id => contacts[id] as SignedUpContact)
        .sort((a, b) =>
          compareString(getUserDisplayName(a), getUserDisplayName(b)),
        ),
    [contacts, contactIds],
  )
}

export function useSignedUpContactIds() {
  const items = useSortedContacts()
  return { loading: false, items }
}

export function useSearchedSignedUpContactIds(query: string) {
  const sortedContacts = useSortedContacts()
  const results = useFuse({
    items: sortedContacts,
    keys: ['firstname', 'lastname', 'phone'],
    query,
  })
  return {
    searchedItems: results,
  }
}

export function useSearchedContacts(query: string): Contact[] {
  const contacts: Record<string, Contact> = useSelector(getContactsView)
  const contactIds = getSignedUpContactIds()

  const contactsData = useMemo(() => {
    return contactIds.map(id => {
      return contacts[id] || ({} as Contact)
    })
  }, [contacts, contactIds])

  return useFuse({
    items: contactsData,
    keys: ['firstname', 'lastname'], // TODO: can use phone while phone is in this client only for who has given permission
    query,
    limit: 5,
  })
}
